/** Phase 3A.1.2 — Dashboard "Library learning velocity" tile.
 *  Fetches GET /api/v1/marketing-intelligence/velocity?days=7 and renders:
 *    - Primary KPI = ai_runs (last 7 days)
 *    - Caption    = "KB promotions: {kb_promotions}"
 *    - Subline    = "Avg quality: {avg_quality_score.toFixed(2)}"
 *    - Top-3 by_agent mini-list (agent_id · count)
 *    - Empty state when total_signals === 0 (DE + EN)
 *  Source of truth for the shape: /app/backend/app/domains/marketing_intelligence/__init__.py @ GET /velocity
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

export default function LibraryLearningVelocityTile() {
  const t = useT();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get("/marketing-intelligence/velocity", { params: { days: 7 } })
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, []);

  if (error) {
    return null; // silent — non-critical tile, do not break dashboard
  }
  if (!data) {
    return (
      <Card data-testid="velocity-tile-loading">
        <CardHeader>
          <CardTitle className="text-base">{t("dashboard_velocity.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
        </CardContent>
      </Card>
    );
  }

  const isEmpty = (data.total_signals || 0) === 0;
  const ai_runs = data.ai_runs || 0;
  const kb_promotions = data.kb_promotions || 0;
  const avg_q =
    typeof data.avg_quality_score === "number" ? data.avg_quality_score : 0;
  const topAgents = (data.by_agent || [])
    .slice()
    .sort((a, b) => (b.count || 0) - (a.count || 0))
    .slice(0, 3);

  return (
    <Card data-testid="velocity-tile">
      <CardHeader className="pb-2">
        <div className="flex items-baseline justify-between">
          <CardTitle className="text-base">{t("dashboard_velocity.title")}</CardTitle>
          <span
            className="text-xs text-muted-foreground"
            data-testid="velocity-tile-caption"
          >
            {t("dashboard_velocity.caption_window")}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {isEmpty ? (
          <p
            className="text-sm text-muted-foreground"
            data-testid="velocity-tile-empty"
          >
            {t("dashboard_velocity.empty")}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
            <div className="md:col-span-1">
              <div
                className="text-4xl font-semibold tabular-nums"
                data-testid="velocity-tile-ai-runs"
              >
                {ai_runs}
              </div>
              <div
                className="mt-1 text-xs text-muted-foreground"
                data-testid="velocity-tile-kb-promotions"
              >
                {t("dashboard_velocity.kb_promotions")}: {kb_promotions}
              </div>
              <div
                className="mt-1 text-xs text-muted-foreground"
                data-testid="velocity-tile-avg-quality"
              >
                {t("dashboard_velocity.avg_quality")}: {avg_q.toFixed(2)}
              </div>
            </div>

            <div className="md:col-span-2 rounded-md border p-3 text-xs space-y-1 bg-muted/20">
              <div className="uppercase tracking-wider text-muted-foreground">
                {t("dashboard_velocity.top_agents")}
              </div>
              {topAgents.length === 0 ? (
                <div className="text-muted-foreground">—</div>
              ) : (
                topAgents.map((a) => (
                  <div
                    key={a.agent_id}
                    className="flex items-center justify-between font-mono text-[11px]"
                    data-testid={`velocity-agent-${a.agent_id}`}
                  >
                    <span>{a.agent_id}</span>
                    <span className="text-muted-foreground">{a.count}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
